<template>
  <app-modal
    ref="modal"
    v-bind="$attrs"
    class="modal-container"
    content-class="modal-medium"
    :title="zoneTitle"
  >
    <template v-slot:body="{ cancel }">
      <validation-observer v-slot="{ handleSubmit }" as="div">
        <v-form novalidate @submit.stop.prevent="handleSubmit(submit)">
          <!-- BODY -->
          <v-card-text class="pa-0">
            <div class="modal-body mt-3 mb-4">
              <v-container>
                <!-- ゾーン名-->
                <v-row>
                  <v-col>
                    <app-badge
                      item="ゾーン名"
                      :isInfo="true"
                      :isRequired="true"
                      :msg="
                        $filter.message(`info_max_length`, { maxLength: 20 })
                      "
                    />
                    <validation-provider
                      v-model="zone.zoneName"
                      name="zoneName"
                      label="ゾーン名"
                      :rules="{ required: true, max: { length: 20 } }"
                      v-slot="{ componentField, errorMessage, meta }"
                    >
                      <app-form-input
                        v-bind="componentField"
                        :error-messages="errorMessage"
                        :field-meta="meta"
                      />
                    </validation-provider>
                  </v-col>
                </v-row>
              </v-container>
            </div>
          </v-card-text>
          <v-divider></v-divider>
          <!-- FOOTER -->
          <v-card-actions>
            <v-list-item class="w-100 pa-1">
              <template v-slot:append>
                <v-btn type="submit" color="primary" variant="flat">{{
                  zoneButton
                }}</v-btn>
                <v-btn @click="cancel" variant="outlined" color="primary"
                  >キャンセル</v-btn
                >
              </template>
            </v-list-item>
          </v-card-actions>
        </v-form>
      </validation-observer>
    </template>
  </app-modal>
</template>

<script lang="ts">
import type { PropType } from "vue";
import { defineComponent } from "vue";
import type { Type1Zone, Type1ZoneEdit } from "@/apis/Type1SiteApi";
import cloneDeep from "lodash/cloneDeep";
import { VueFinalModal } from "vue-final-modal";

export default defineComponent({
  name: "ZoneEdit",
  props: {
    /** ゾーンモード */
    zoneMode: {
      type: String as PropType<
        "global" | "extra" | "privateRegister" | "privateModify"
      >,
    },
    /**
     * 表示用ゾーン
     * 新規の場合nullになる
     */
    zoneDate: {
      type: Object as PropType<Type1Zone | null>,
      default: null,
    },
    /** 排他確認用パラメータ */
    type1SiteZoneKeyId: {
      type: String,
    },
  },
  data() {
    return {
      /** モーダル用タイトル */
      zoneTitle: "" as string,
      /** モーダル用ボタン */
      zoneButton: "" as string,
      /** 表示用ゾーン */
      zone: this.zoneDate
        ? cloneDeep(this.zoneDate)
        : ({
            zoneId: "",
            zoneName: "",
          } as Type1Zone),
    };
  },

  mounted() {
    this.load();
  },

  methods: {
    /** 初期化機能 */
    load() {
      this.changeTB();
    },
    /** タイトルとボタンの表示変換 */
    changeTB() {
      switch (this.zoneMode) {
        case "global":
          this.zoneTitle = "グローバルゾーン設定(Type1、イントラネットFW)";
          this.zoneButton = "設定";
          break;
        case "extra":
          this.zoneTitle = "エクストラゾーン設定(Type1、イントラネットFW)";
          this.zoneButton = "設定";
          break;
        case "privateRegister":
          this.zoneTitle = "プライベートゾーン設定(Type1、イントラネットFW)";
          this.zoneButton = "追加";
          break;
        case "privateModify":
          this.zoneTitle = "プライベートゾーン設定(Type1、イントラネットFW)";
          this.zoneButton = "変更";
          break;
      }
    },
    /** 追加、変更、設定機能 */
    async submit() {
      // 追加・変更データの設定
      const reqData: Type1ZoneEdit = {
        zoneName: this.zone.zoneName,
        type1SiteZoneKeyId: this.type1SiteZoneKeyId!,
      };
      // モードによってメソッドを使用
      switch (this.zoneMode) {
        case "global":
          await this.$confirm(
            "グローバルゾーン名を設定します。よろしいですか？"
          );
          if (this.zoneDate) {
            // グローバルゾーン変更
            await this.$api.type1SiteZone.postType1GlobalZoneModify(
              reqData,
              this.zone.zoneId
            );
          } else {
            // グローバルゾーン追加
            await this.$api.type1SiteZone.postType1GlobalZoneRegister(reqData);
          }
          (this.$refs.modal as typeof VueFinalModal & { ok: () => void }).ok();
          break;
        case "extra":
          await this.$confirm(
            "エクストラゾーン名を設定します。よろしいですか？"
          );
          if (this.zoneDate) {
            // エクストラゾーン変更
            await this.$api.type1SiteZone.postType1ExtraZoneModify(
              reqData,
              this.zone.zoneId
            );
          } else {
            // エクストラゾーン追加
            await this.$api.type1SiteZone.postType1ExtraZoneRegister(reqData);
          }
          (this.$refs.modal as typeof VueFinalModal & { ok: () => void }).ok();
          break;
        case "privateRegister":
          await this.$confirm(
            "プライベートゾーンを追加します。よろしいですか？"
          );
          await this.$api.type1SiteZone.postType1PrivateZoneRegister(reqData);
          (this.$refs.modal as typeof VueFinalModal & { ok: () => void }).ok();
          break;
        case "privateModify":
          await this.$confirm(
            "プライベートゾーン名を変更します。よろしいですか？"
          );
          await this.$api.type1SiteZone.postType1PrivateZoneModify(
            reqData,
            this.zone.zoneId
          );
          (this.$refs.modal as typeof VueFinalModal & { ok: () => void }).ok();
          break;
      }
    },
  },
});
</script>

<style lang="scss" scoped></style>
